import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { Slottable } from '../slot';
import { ToggleButtonIcon } from './toggle-button-icon';
import { ToggleButtonLabel } from './toggle-button-label';
import { ToggleButtonLoading } from './toggle-button-loading';
import { ToggleButtonRoot } from './toggle-button-root';
import type { ToggleButtonPropsInterface } from './types/toggle-button.types';

/**
 * @since 8.0.0
 */
export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonPropsInterface>(
  (props: ToggleButtonPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      icon,
      iconPosition = 'start',
      loading = false,
      ...rest
    } = props;

    return (
      <ToggleButton.Root ref={forwardedRef} asChild={asChild} loading={loading} {...rest}>
        <Slottable child={children}>
          {(slottable) => (
            <Fragment>
              {icon && iconPosition === 'start' ? (
                <ToggleButton.Icon>{icon}</ToggleButton.Icon>
              ) : null}
              <ToggleButton.Label>{slottable}</ToggleButton.Label>
              {icon && iconPosition === 'end' ? (
                <ToggleButton.Icon>{icon}</ToggleButton.Icon>
              ) : null}
              {loading ? <ToggleButton.Loading /> : null}
            </Fragment>
          )}
        </Slottable>
      </ToggleButton.Root>
    );
  },
) as ForwardRefExoticComponent<ToggleButtonPropsInterface> & {
  Root: typeof ToggleButtonRoot;
  Label: typeof ToggleButtonLabel;
  Icon: typeof ToggleButtonIcon;
  Loading: typeof ToggleButtonLoading;
};

ToggleButton.displayName = 'ToggleButton';

ToggleButton.Root = ToggleButtonRoot;
ToggleButton.Label = ToggleButtonLabel;
ToggleButton.Icon = ToggleButtonIcon;
ToggleButton.Loading = ToggleButtonLoading;
